import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Camera, 
  MousePointer, 
  Type, 
  Tag,
  X,
  Plus
} from 'lucide-react';

interface FaceDetection {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
  bibNumber?: string;
}

interface InteractivePhotoPreviewProps {
  photo: File;
  onTextExtracted?: (text: string, area: { x: number; y: number; width: number; height: number }) => void;
  onBibNumberMarked?: (bibNumber: string, area: { x: number; y: number; width: number; height: number }) => void;
  faceDetections?: FaceDetection[];
  onFaceDetectionUpdate?: (faces: FaceDetection[]) => void;
}

export const InteractivePhotoPreview = ({ 
  photo, 
  onTextExtracted, 
  onBibNumberMarked,
  faceDetections = [],
  onFaceDetectionUpdate
}: InteractivePhotoPreviewProps) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{ x: number; y: number } | null>(null);
  const [showBibInput, setShowBibInput] = useState(false);
  const [bibInputPosition, setBibInputPosition] = useState<{ x: number; y: number } | null>(null);
  const [bibInputValue, setBibInputValue] = useState('');
  const [faces, setFaces] = useState<FaceDetection[]>(faceDetections);
  const [autoOpenMenu, setAutoOpenMenu] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Carregar imagem
  useEffect(() => {
    const url = URL.createObjectURL(photo);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);


  // Atualizar faces quando prop mudar
  useEffect(() => {
    setFaces(faceDetections);
  }, [faceDetections]);

  // Desenhar faces detectadas no canvas
  const drawFaces = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Limpar canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Desenhar faces
    faces.forEach((face, index) => {
      const scaleX = canvas.width / image.naturalWidth;
      const scaleY = canvas.height / image.naturalHeight;

      const x = face.x * scaleX;
      const y = face.y * scaleY;
      const width = face.width * scaleX;
      const height = face.height * scaleY;

      // Desenhar retângulo da face
      ctx.strokeStyle = face.bibNumber ? '#10b981' : '#ef4444';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, width, height);

      // Desenhar número da bib se existir
      if (face.bibNumber) {
        ctx.fillStyle = 'rgba(16, 185, 129, 0.3)'; // Verde mais transparente
        ctx.fillRect(x, y - 20, Math.max(60, face.bibNumber.length * 10), 20);
        ctx.fillStyle = 'white';
        ctx.font = '12px Arial';
        ctx.fillText(face.bibNumber, x + 5, y - 5);
      }

      // Desenhar número da face
      ctx.fillStyle = face.bibNumber ? '#10b981' : '#ef4444';
      ctx.fillRect(x + width - 20, y, 20, 20);
      ctx.fillStyle = 'white';
      ctx.font = '12px Arial';
      ctx.fillText((index + 1).toString(), x + width - 15, y + 15);
    });
  }, [faces]);

  // Redimensionar canvas quando imagem carregar
  const handleImageLoad = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    canvas.width = image.offsetWidth;
    canvas.height = image.offsetHeight;
    drawFaces();
  };

  // Clique direto para abrir menu
  const handleClick = (e: React.MouseEvent) => {
    if (e.target !== canvasRef.current) return;
    
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Salvar posição do clique para referência
    setSelectionStart({ x, y });
    setSelectionEnd({ x, y });
    
    // Mostrar menu dropdown diretamente
    setBibInputPosition({ x: e.clientX, y: e.clientY });
    
    // Marcar para abrir menu automaticamente
    setAutoOpenMenu(true);
  };


  // Extrair texto da área selecionada
  const handleExtractText = () => {
    if (!selectionStart || !selectionEnd) return;
    
    const image = imageRef.current;
    if (!image) return;

    const scaleX = image.naturalWidth / image.offsetWidth;
    const scaleY = image.naturalHeight / image.offsetHeight;
    
    const x = Math.min(selectionStart.x, selectionEnd.x) * scaleX;
    const y = Math.min(selectionStart.y, selectionEnd.y) * scaleY;
    const width = Math.abs(selectionEnd.x - selectionStart.x) * scaleX;
    const height = Math.abs(selectionEnd.y - selectionStart.y) * scaleY;
    
    if (onTextExtracted) {
      onTextExtracted('', { x, y, width, height });
    }
    
    setBibInputPosition(null);
    setAutoOpenMenu(false);
  };

  // Marcar bib number
  const handleMarkBib = () => {
    setShowBibInput(true);
    setBibInputValue('');
    setAutoOpenMenu(false);
  };

  // Confirmar bib number
  const handleConfirmBib = () => {
    if (!bibInputValue.trim() || !selectionStart || !selectionEnd) return;
    
    const image = imageRef.current;
    if (!image) return;

    const scaleX = image.naturalWidth / image.offsetWidth;
    const scaleY = image.naturalHeight / image.offsetHeight;
    
    const x = Math.min(selectionStart.x, selectionEnd.x) * scaleX;
    const y = Math.min(selectionStart.y, selectionEnd.y) * scaleY;
    const width = Math.abs(selectionEnd.x - selectionStart.x) * scaleX;
    const height = Math.abs(selectionEnd.y - selectionStart.y) * scaleY;
    
    // Adicionar nova face detectada
    const newFace: FaceDetection = {
      id: Date.now().toString(),
      x,
      y,
      width,
      height,
      confidence: 1.0,
      bibNumber: bibInputValue.trim()
    };
    
    const updatedFaces = [...faces, newFace];
    setFaces(updatedFaces);
    
    // Atualizar desenho imediatamente
    drawFaces();
    
    if (onFaceDetectionUpdate) {
      onFaceDetectionUpdate(updatedFaces);
    }
    
    if (onBibNumberMarked) {
      onBibNumberMarked(bibInputValue.trim(), { x, y, width, height });
    }
    
    setShowBibInput(false);
    setBibInputValue('');
    setBibInputPosition(null);
  };

  // Cancelar input de bib
  const handleCancelBib = () => {
    setShowBibInput(false);
    setBibInputValue('');
    setBibInputPosition(null);
  };

  // Remover face
  const removeFace = (faceId: string) => {
    const updatedFaces = faces.filter(face => face.id !== faceId);
    setFaces(updatedFaces);
    
    if (onFaceDetectionUpdate) {
      onFaceDetectionUpdate(updatedFaces);
    }
  };

  return (
    <Card className="w-full h-full bg-black border-black">
      <CardHeader className="bg-black border-b border-gray-800">
        <CardTitle className="flex items-center space-x-2 text-white">
          <Camera className="h-5 w-5" />
          <span>Preview Interativo</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="bg-black p-0">
        <div className="space-y-4 p-4">
          {/* Lista de bib numbers no topo */}
          {faces.length > 0 && (
            <div className="space-y-2">
              <Label className="text-white">Bib Numbers Marcados ({faces.filter(f => f.bibNumber).length})</Label>
              <div className="flex flex-wrap gap-2">
                {faces.filter(face => face.bibNumber).map((face, index) => (
                  <Badge
                    key={face.id}
                    variant="default"
                    className="cursor-pointer bg-green-600 hover:bg-green-700"
                    onClick={() => removeFace(face.id)}
                  >
                    Bib {face.bibNumber}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Preview da imagem */}
          <div className="relative bg-black rounded-lg overflow-hidden" ref={containerRef}>
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Preview"
              className="max-w-full h-auto"
              onLoad={handleImageLoad}
            />
            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 cursor-pointer"
              onClick={handleClick}
            />
          </div>

          {/* Menu dropdown */}
          {bibInputPosition && !showBibInput && (
            <div
              className="fixed z-50"
              style={{
                left: bibInputPosition.x,
                top: bibInputPosition.y,
              }}
            >
              <DropdownMenu open={autoOpenMenu} onOpenChange={setAutoOpenMenu}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MousePointer className="h-4 w-4 mr-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={handleExtractText}>
                    <Type className="h-4 w-4 mr-2" />
                    Extrair Texto
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleMarkBib}>
                    <Tag className="h-4 w-4 mr-2" />
                    Marcar Bib Number
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Input flutuante para bib number */}
          {showBibInput && (
            <div
              className="fixed z-50 bg-white border rounded-lg shadow-lg p-3"
              style={{
                left: bibInputPosition?.x || 0,
                top: bibInputPosition?.y || 0,
              }}
            >
              <div className="flex items-center space-x-2">
                <Input
                  value={bibInputValue}
                  onChange={(e) => setBibInputValue(e.target.value)}
                  placeholder="Digite o bib number"
                  className="w-32"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleConfirmBib();
                    }
                  }}
                />
                <Button size="sm" onClick={handleConfirmBib}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

        </div>
      </CardContent>
    </Card>
  );
};
